import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import axios from "axios";
import { 
  TrendingUp,
  TrendingDown, 
  MessageSquare, 
  Shield, 
  BarChart3,
  ArrowRight,
  Check,
  Lock,
  Crown,
  Users,
  Zap,
  ExternalLink,
  RefreshCw,
  Gem,
  Bitcoin,
  Mail,
  Mic,
  FileText,
  Video,
  Briefcase,
  Clock,
  ChevronRight,
  X,
  CreditCard,
  Sparkles,
  Brain,
  LineChart,
  UserPlus,
  AlertTriangle,
  Download,
  Smartphone
} from "lucide-react";

const LOGO_URL = "/doslions-logo.png";
const API = process.env.REACT_APP_BACKEND_URL;

// Mini Candlestick Chart Component - Bar style with trend line
const MiniCandleChart = ({ data, width = 120, height = 45 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center rounded" style={{ width, height, backgroundColor: 'rgba(20,20,20,0.8)' }}>
        <div className="w-4 h-4 border-2 border-zinc-600 border-t-amber-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const closes = data.map(c => c.close);
  const minClose = Math.min(...closes);
  const maxClose = Math.max(...closes);
  const range = maxClose - minClose || 1;

  const candleCount = data.length;
  const padding = 6;
  const chartHeight = height - padding * 2;
  const chartWidth = width - padding * 2;
  const barWidth = Math.max(4, Math.floor(chartWidth / candleCount) - 2);
  const gap = (chartWidth - barWidth * candleCount) / (candleCount - 1 || 1);
  
  // Overall trend color
  const trendUp = closes[closes.length - 1] >= closes[0];
  const trendColor = trendUp ? "#22c55e" : "#ef4444";

  return (
    <svg 
      width={width} 
      height={height} 
      viewBox={`0 0 ${width} ${height}`}
      style={{ borderRadius: '4px', backgroundColor: 'rgba(15,15,15,0.9)' }}
    >
      {/* Bars with smooth transition */}
      {closes.map((close, i) => {
        const x = padding + i * (barWidth + gap);
        const normalizedHeight = ((close - minClose) / range) * chartHeight;
        const barHeight = Math.max(4, normalizedHeight) || chartHeight * 0.5;
        const y = padding + chartHeight - barHeight;
        const prevClose = i > 0 ? closes[i - 1] : close;
        const barColor = close >= prevClose ? "#22c55e" : "#ef4444";
        const isLast = i === closes.length - 1;

        return (
          <rect 
            key={i} 
            x={x} 
            y={y} 
            width={barWidth} 
            height={barHeight} 
            fill={barColor} 
            rx="1" 
            opacity={isLast ? "1" : "0.85"}
            style={{ transition: 'all 0.3s ease-out' }}
          >
            {isLast && <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />}
          </rect>
        );
      })}
      
      {/* Trend line */}
      <polyline
        points={closes.map((close, i) => {
          const x = padding + i * (barWidth + gap) + barWidth / 2;
          const normalizedY = ((close - minClose) / range) * chartHeight;
          const y = padding + chartHeight - (normalizedY || chartHeight * 0.5);
          return `${x},${y}`;
        }).join(' ')}
        fill="none"
        stroke={trendColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
        style={{ transition: 'all 0.3s ease-out' }}
      />
      
      {/* Live indicator dot on last point */}
      <circle
        cx={padding + (closes.length - 1) * (barWidth + gap) + barWidth / 2}
        cy={padding + chartHeight - (((closes[closes.length - 1] - minClose) / range) * chartHeight || chartHeight * 0.5)}
        r="3"
        fill={trendColor}
        opacity="0.9"
      >
        <animate attributeName="r" values="2;4;2" dur="1.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

// Galaxy Stars Animation Component
const GalaxyStars = () => {
  // Pre-computed star positions (deterministic — no re-render glitch)
  const stars = Array.from({ length: 60 }, (_, i) => ({
    w: ((i * 7 + 3) % 3) + 1,
    top: ((i * 37 + 13) % 100),
    left: ((i * 53 + 7) % 100),
    opacity: 0.3 + ((i * 17) % 7) / 10,
    dur: 2 + ((i * 11) % 3),
    delay: ((i * 23) % 20) / 10
  }));
  const movers = Array.from({ length: 12 }, (_, i) => ({
    w: ((i * 7 + 2) % 3) + 1,
    top: ((i * 31 + 5) % 100),
    color: i % 3 === 0 ? '#F59E0B' : i % 3 === 1 ? '#FBBF24' : '#ffffff',
    opacity: 0.2 + ((i * 13) % 5) / 10,
    dur: 12 + ((i * 7) % 10),
    delay: ((i * 19) % 10)
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      <div className="absolute inset-0">
        {stars.map((s, i) => (
          <div
            key={`s-${i}`}
            className="absolute rounded-full bg-white"
            style={{
              width: s.w + 'px', height: s.w + 'px',
              top: s.top + '%', left: s.left + '%',
              opacity: s.opacity,
              animation: `twinkle ${s.dur}s ease-in-out infinite`,
              animationDelay: s.delay + 's'
            }}
          />
        ))}
      </div>
      <div className="absolute inset-0">
        {movers.map((m, i) => (
          <div
            key={`m-${i}`}
            className="absolute rounded-full"
            style={{
              width: m.w + 'px', height: m.w + 'px',
              top: m.top + '%', left: '-5%',
              backgroundColor: m.color,
              opacity: m.opacity,
              animation: `shootingStar ${m.dur}s linear infinite`,
              animationDelay: m.delay + 's'
            }}
          />
        ))}
      </div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-amber-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes shootingStar {
          0% { transform: translateX(0) translateY(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(110vw) translateY(20vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [indices, setIndices] = useState([]);
  const [candleData, setCandleData] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showServicePopup, setShowServicePopup] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showProPopup, setShowProPopup] = useState(false);
  const [showFreeBlocker, setShowFreeBlocker] = useState(false);
  const [freeViewStartTime, setFreeViewStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [socialProofVisible, setSocialProofVisible] = useState(false);
  const [socialProofIndex, setSocialProofIndex] = useState(0);
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0 });

  // Social proof messages — cycling toasts
  const socialProofMessages = [
    { name: "Carlos M.", location: "Miami", action: "just joined Pro Ecopreneur", time: "2 min ago" },
    { name: "Sarah K.", location: "New York", action: "unlocked AI Hot Picks", time: "5 min ago" },
    { name: "James R.", location: "London", action: "just became Pro Ecopreneur", time: "8 min ago" },
    { name: "Maria L.", location: "Los Angeles", action: "activated Whale Tracker", time: "12 min ago" },
    { name: "David W.", location: "Toronto", action: "joined Pro Ecopreneur", time: "15 min ago" },
    { name: "Ana P.", location: "Houston", action: "unlocked AI Earnings Whisper", time: "18 min ago" },
  ];

  // Countdown timer — resets daily at midnight EST
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const estOffset = -5;
      const utcHour = now.getUTCHours();
      const estHour = (utcHour + estOffset + 24) % 24;
      const remainH = (23 - estHour + 24) % 24;
      const remainM = 59 - now.getUTCMinutes();
      const remainS = 59 - now.getUTCSeconds();
      setCountdown({ h: remainH, m: remainM, s: remainS });
    };
    updateCountdown();
    const t = setInterval(updateCountdown, 1000);
    return () => clearInterval(t);
  }, []);

  // Social proof pop-up cycle
  useEffect(() => {
    if (user) return;
    const showToast = () => {
      setSocialProofVisible(true);
      setTimeout(() => setSocialProofVisible(false), 4000);
      setTimeout(() => setSocialProofIndex(i => (i + 1) % socialProofMessages.length), 5000);
    };
    const delay = setTimeout(showToast, 6000);
    const interval = setInterval(showToast, 12000);
    return () => { clearTimeout(delay); clearInterval(interval); };
  }, [user]);

  // Check if free view time has expired (1 hour limit)
  useEffect(() => {
    if (!user && freeViewStartTime) {
      const checkTime = () => {
        const elapsed = Date.now() - freeViewStartTime;
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        const remaining = oneHour - elapsed;
        
        if (remaining <= 0) {
          setShowFreeBlocker(true);
          setTimeRemaining(0);
        } else {
          setTimeRemaining(Math.floor(remaining / 1000 / 60)); // minutes remaining
        }
      };
      
      checkTime();
      const interval = setInterval(checkTime, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user, freeViewStartTime]);

  // Start free view timer when navigating to markets
  const handleFreeMarketsView = () => {
    if (!user) {
      // Set the start time for free viewing
      const storedStartTime = localStorage.getItem('freeViewStartTime');
      if (storedStartTime) {
        setFreeViewStartTime(parseInt(storedStartTime));
        const elapsed = Date.now() - parseInt(storedStartTime);
        if (elapsed >= 60 * 60 * 1000) {
          // Time expired, show blocker
          setShowFreeBlocker(true);
          return;
        }
      } else {
        const now = Date.now();
        localStorage.setItem('freeViewStartTime', now.toString());
        setFreeViewStartTime(now);
      }
    }
    navigate("/markets");
  };

  // Reset free view timer (allows another hour)
  const resetFreeViewTimer = () => {
    const now = Date.now();
    localStorage.setItem('freeViewStartTime', now.toString());
    setFreeViewStartTime(now);
    setShowFreeBlocker(false);
  };

  // Service popup handler
  const openServicePopup = (service) => {
    setSelectedService(service);
    setShowServicePopup(true);
  };

  const fetchMarketData = async () => {
    // Don't show loading spinner on refresh - keeps UI smooth
    const isInitialLoad = indices.length === 0;
    if (isInitialLoad) setLoading(true);
    
    try {
      // Fetch ALL data in parallel including candle charts
      const indexSymbols = ["^GSPC", "^DJI", "^IXIC", "^RUT", "^VIX"];
      
      const [stocksRes, commoditiesRes, cryptoRes, indicesRes, candleRes] = await Promise.all([
        axios.get(`${API}/api/stocks`),
        axios.get(`${API}/api/commodities`),
        axios.get(`${API}/api/crypto`),
        axios.get(`${API}/api/indices`),
        axios.post(`${API}/api/mini-candles`, indexSymbols).catch(() => ({ data: { candles: {} } }))
      ]);
      
      // Update all state at once for synchronized display
      setStocks(stocksRes.data.stocks || []);
      setCommodities(commoditiesRes.data.commodities || []);
      setCrypto(cryptoRes.data.crypto || []);
      setIndices(indicesRes.data.indices || []);
      
      // Sync candle data with current prices
      if (candleRes.data.candles) {
        const syncedCandles = { ...candleRes.data.candles };
        
        // Update the last candle's close price to match current live price
        const indicesData = indicesRes.data.indices || [];
        const symbolMap = {
          'S&P 500': '^GSPC',
          'Dow Jones': '^DJI', 
          'NASDAQ': '^IXIC',
          'Russell 2000': '^RUT',
          'Volatility Index': '^VIX'
        };
        
        indicesData.forEach(idx => {
          const symbol = symbolMap[idx.name];
          if (symbol && syncedCandles[symbol] && syncedCandles[symbol].length > 0) {
            // Update the last candle to match current price
            const lastCandle = syncedCandles[symbol][syncedCandles[symbol].length - 1];
            const currentPrice = parseFloat(idx.price?.toString().replace(/,/g, '')) || lastCandle.close;
            lastCandle.close = currentPrice;
            lastCandle.high = Math.max(lastCandle.high, currentPrice);
            lastCandle.low = Math.min(lastCandle.low, currentPrice);
          }
        });
        
        setCandleData(syncedCandles);
      }
      
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error("Failed to fetch market data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketData();
    // Auto-refresh every 2 minutes for fresh data
    const interval = setInterval(fetchMarketData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "LIVE MARKET PRICES",
      description: "AI-powered stocks, commodities & crypto insights at your fingertips",
      service: "markets"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "LIONAI ANALYST",
      description: "Ask anything about markets, get expert AI insights",
      service: "lionai"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "GLOBAL COVERAGE",
      description: "Track stocks, gold, oil, crypto and more",
      service: "coverage"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "ECOPRENEUR MINDSET",
      description: "Smart project development for sustainable growth",
      service: "ecopreneur"
    }
  ];

  // Service details for popup
  const serviceDetails = {
    markets: {
      title: "AI Market Insights",
      subtitle: "Get AI-powered market analysis",
      options: [
        { name: "Pro Ecopreneur", price: "$19.97", desc: "First month, then $49.97/mo", primary: true, action: () => navigate("/auth") },
        { name: "See AI Features", price: "From $19.97", desc: "6 AI engines included in Pro", action: () => document.getElementById('ai-features')?.scrollIntoView({ behavior: 'smooth' }) }
      ]
    },
    lionai: {
      title: "MEET LIONAI",
      subtitle: "Your AI market analyst powered by intelligence",
      options: [
        { name: "Pro Ecopreneur", price: "$19.97", desc: "First month, then $49.97/mo", primary: true, action: () => navigate("/auth") },
        { name: "Explore AI Features", price: "From $19.97", desc: "All AI tools included in Pro", action: () => document.getElementById('ai-features')?.scrollIntoView({ behavior: 'smooth' }) }
      ]
    },
    coverage: {
      title: "Global Market Coverage",
      subtitle: "Track any market worldwide",
      options: [
        { name: "Pro Ecopreneur", price: "$19.97", desc: "First month, then $49.97/mo", primary: true, action: () => navigate("/auth") }
      ]
    },
    ecopreneur: {
      title: "Ecopreneur Growth Suite",
      subtitle: "Build sustainable success",
      options: [
        { name: "Pro Ecopreneur", price: "$19.97", desc: "First month, then $49.97/mo", primary: true, action: () => navigate("/auth") }
      ]
    }
  };

  const freeFeatures = [
    "AI-curated stock prices (8 stocks)",
    "Commodity market insights (6 commodities)",
    "Basic market overview",
    "Personal watchlist"
  ];

  const proFeatures = [
    "AI EXPERIENCE + AI DAILY SIGNALS",
    "MEET LIONAI (Pay to build)",
    "20+ Trending & Opportunity Stocks",
    "Extended Commodities & Crypto",
    "Daily Market News Feed",
    "Weekly AI Trending Picks",
    "Deep market analysis",
    "Search any asset",
    "Priority support",
    "Cancel anytime"
  ];
  
  // Legal disclaimer
  const legalDisclaimer = "* AI Daily Futurist Numbers Calculation Prediction + Daily Signal. NOT FINANCIAL ADVICE. For educational purposes only.";

  const pricingNote = "First month $19.97, then $49.97/mo";

  // QR Code Popup State
  const [showQRPopup, setShowQRPopup] = useState(false);

  // QR Code Popup Component
  const QRCodePopup = () => {
    if (!showQRPopup) return null;
    
    return (
      <div 
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
        onClick={() => setShowQRPopup(false)}
      >
        <div 
          className="relative bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-emerald-500/30 rounded-3xl p-8 max-w-sm w-full shadow-[0_0_60px_rgba(16,185,129,0.3)] animate-in zoom-in-95 duration-300"
          onClick={e => e.stopPropagation()}
        >
          {/* Close Button */}
          <button 
            onClick={() => setShowQRPopup(false)}
            className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Alien Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="text-4xl animate-bounce" style={{ animationDuration: '2s' }}>🛸</span>
              <span className="text-2xl">👽</span>
              <span className="text-4xl animate-bounce" style={{ animationDuration: '2s', animationDelay: '0.5s' }}>🛸</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
              Scan to <span className="text-emerald-400">Install</span>
            </h3>
            <p className="text-sm text-zinc-400">Point your phone camera at the code</p>
          </div>
          
          {/* QR Code Container */}
          <div className="relative bg-black rounded-2xl p-4 border border-emerald-500/20 mb-6">
            {/* Corner decorations */}
            <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-emerald-500 rounded-tl-lg"></div>
            <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-emerald-500 rounded-tr-lg"></div>
            <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-emerald-500 rounded-bl-lg"></div>
            <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-emerald-500 rounded-br-lg"></div>
            
            {/* QR Code Image */}
            <img 
              src="/qr-install.png" 
              alt="Scan to install ECOLIONS" 
              className="w-full h-auto rounded-lg"
            />
            
            {/* Scanning animation overlay */}
            <div className="absolute inset-4 pointer-events-none overflow-hidden rounded-lg">
              <div 
                className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-60"
                style={{ 
                  animation: 'scan 2s ease-in-out infinite',
                }}
              />
            </div>
          </div>
          
          {/* Instructions */}
          <div className="space-y-2 mb-6">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <span className="text-emerald-400 text-xs">1</span>
              </div>
              <span className="text-zinc-300">Open camera on your phone</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <span className="text-emerald-400 text-xs">2</span>
              </div>
              <span className="text-zinc-300">Point at QR code</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center">
                <span className="text-amber-400 text-xs">3</span>
              </div>
              <span className="text-zinc-300">Tap notification to install</span>
            </div>
          </div>
          
          {/* Powered by DOSLIONS */}
          <div className="text-center pt-4 border-t border-zinc-800">
            <div className="flex items-center justify-center gap-2">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Powered by</span>
              <img src={LOGO_URL} alt="DOSLIONS" className="h-5 w-5" />
              <span className="text-xs font-bold text-amber-500">DOSLIONS</span>
            </div>
          </div>
          
          {/* Sparkles */}
          <div className="absolute -top-2 -left-2 text-yellow-400 text-lg animate-pulse">✨</div>
          <div className="absolute -top-2 -right-2 text-cyan-400 text-lg animate-pulse" style={{ animationDelay: '0.5s' }}>✨</div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-emerald-400 text-sm animate-pulse" style={{ animationDelay: '1s' }}>⭐</div>
        </div>
        
        {/* Add scanning animation keyframes */}
        <style>{`
          @keyframes scan {
            0%, 100% { top: 0; }
            50% { top: calc(100% - 4px); }
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      {/* Galaxy Stars Background */}
      <GalaxyStars />
      
      {/* QR Code Popup */}
      <QRCodePopup />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => user ? navigate("/dashboard") : window.scrollTo({ top: 0, behavior: 'smooth' })}
              data-testid="logo-home-link"
            >
              <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
              <div>
                <span className="font-bold text-xl tracking-tight text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                  ECOLIONS
                </span>
                <span className="font-bold text-xl tracking-tight text-white ml-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  AI MARKETS
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate("/auth")}
                    className="text-zinc-400 hover:text-white"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate("/auth")}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* WHAT YOU'RE MISSING — Live Scrolling Ticker */}
      {!user && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-black/95 border-b border-amber-500/30 overflow-hidden" data-testid="live-ticker">
          <div className="flex animate-scroll-ticker">
            {[...Array(2)].map((_, dupeIdx) => (
              <div key={dupeIdx} className="flex shrink-0 items-center gap-8 px-4 py-2">
                {[
                  { text: "NVDA just triggered a BUY signal", color: "text-emerald-400" },
                  { text: "Whale Alert: $2.4M BTC purchase detected", color: "text-amber-400" },
                  { text: "TSLA sentiment shifted BULLISH", color: "text-emerald-400" },
                  { text: "AI Hot Pick: AAPL momentum +3.2%", color: "text-cyan-400" },
                  { text: "Insider Buy: MSFT $1.8M purchase", color: "text-amber-400" },
                  { text: "GOOGL earnings whisper: BEAT expected", color: "text-emerald-400" },
                  { text: "Social Sentiment: AMZN trending bullish", color: "text-purple-400" },
                  { text: "Economic Alert: Fed decision in 2 days", color: "text-red-400" },
                  { text: "SOL momentum surge +5.4% detected", color: "text-cyan-400" },
                  { text: "Gold breaks resistance — AI says HOLD", color: "text-amber-400" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 shrink-0">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className={`text-xs font-bold whitespace-nowrap ${item.color}`}>{item.text}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <style>{`
            @keyframes scrollTicker {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-scroll-ticker {
              animation: scrollTicker 40s linear infinite;
            }
            .animate-scroll-ticker:hover {
              animation-play-state: paused;
            }
          `}</style>
        </div>
      )}

      {/* SOCIAL PROOF POP-UP TOAST */}
      {!user && socialProofVisible && (
        <div 
          className="fixed bottom-6 left-6 z-[100] max-w-xs animate-in slide-in-from-bottom-5 fade-in duration-500"
          data-testid="social-proof-toast"
        >
          <div className="bg-[#111111] border border-amber-500/40 p-4 shadow-[0_0_25px_rgba(245,158,11,0.15)] rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-amber-500/20 rounded-full flex items-center justify-center shrink-0">
                <span className="text-amber-500 font-bold text-sm">{socialProofMessages[socialProofIndex].name[0]}</span>
              </div>
              <div>
                <p className="text-sm text-white font-semibold">{socialProofMessages[socialProofIndex].name}
                  <span className="text-zinc-500 font-normal text-xs ml-1">from {socialProofMessages[socialProofIndex].location}</span>
                </p>
                <p className="text-xs text-amber-400 font-bold">{socialProofMessages[socialProofIndex].action}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">{socialProofMessages[socialProofIndex].time}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className={`relative ${user ? 'pt-32' : 'pt-40'} pb-20 px-4 overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,158,11,0.15),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(16,185,129,0.1),transparent_50%)]"></div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Sponsor Banner */}
          <a 
            href="https://www.doslions.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3 bg-amber-500/10 border border-amber-500/30 mb-8 hover:bg-amber-500/20 transition-colors group"
          >
            <img src={LOGO_URL} alt="DOSLIONS" className="w-8 h-8 object-contain" />
            <span className="text-sm tracking-wider text-amber-500 font-bold uppercase">
              Powered by doslions.com
            </span>
            <ExternalLink className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
          </a>
          
          <h1 
            className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tighter uppercase mb-6"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            <span className="text-amber-500">ECO</span>LIONS
            <br />
            <span className="text-emerald-500">AI</span> MARKETS
          </h1>
          
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-4" style={{ fontFamily: 'Manrope' }}>
            AI-powered market insights. <span className="text-amber-500 font-bold">LIONAI</span>-powered predictions. 
            <span className="text-amber-500"> Ecopreneur mindset.</span>
          </p>
          <p className="text-sm text-zinc-500 max-w-xl mx-auto mb-6">
            Track stocks, commodities, and global markets. Unlock LIONAI to save time on your project research.
          </p>

          {/* Social Proof */}
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-amber-500" />
              <span className="text-zinc-400"><span className="text-white font-bold">500+</span> ecopreneurs joined</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span className="text-zinc-400"><span className="text-white font-bold">AI-powered</span> insights</span>
            </div>
          </div>
          
          {/* TWO CTA BUTTONS */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* OPTION A: Pro Ecopreneur - Primary CTA */}
            <Button 
              onClick={() => setShowProPopup(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-8 py-6 text-lg shadow-[0_0_30px_rgba(245,158,11,0.4)] relative overflow-hidden group"
              data-testid="hero-pro-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Become Pro Ecopreneur
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </Button>
            
            {/* OPTION B: Free Markets View */}
            <Button 
              onClick={handleFreeMarketsView}
              variant="outline"
              className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10 font-bold uppercase tracking-wider px-8 py-6 text-lg"
              data-testid="hero-free-btn"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Free Markets View
            </Button>
          </div>
          
          {/* INSTALL APP BUTTON - PWA */}
          <div className="mt-6 flex items-center justify-center">
            <button
              onClick={() => {
                // Trigger PWA install or show instructions
                if (window.deferredPrompt) {
                  window.deferredPrompt.prompt();
                } else {
                  // Show install instructions modal
                  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                  const message = isIOS 
                    ? "Tap the Share button below, then 'Add to Home Screen'" 
                    : "Click the menu (⋮) and select 'Install App' or 'Add to Home Screen'";
                  alert(`📱 Install ECOLIONS App\n\n${message}`);
                }
              }}
              className="group flex items-center gap-2 px-4 py-2 bg-zinc-900/80 border border-zinc-700 hover:border-emerald-500/50 rounded-full transition-all hover:bg-emerald-500/10"
              data-testid="install-app-btn"
            >
              <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Download className="w-3.5 h-3.5 text-emerald-500" />
              </div>
              <span className="text-sm font-medium text-zinc-300 group-hover:text-emerald-400 transition-colors">
                Install App
              </span>
              <span className="text-xs text-zinc-600 hidden sm:inline">• Free</span>
            </button>
          </div>
          
          <p className="text-xs text-zinc-600 mt-4">
            Pro Ecopreneur: $19.97 first month • Free view: 1 hour live access
          </p>

          {/* URGENCY COUNTDOWN TIMER */}
          {!user && (
            <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="countdown-timer">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-red-400 font-bold uppercase tracking-wider">Today's AI Signals expire in</span>
              <div className="flex items-center gap-1 font-mono">
                <span className="bg-black/60 text-red-400 font-bold text-sm px-2 py-1 rounded">{String(countdown.h).padStart(2,'0')}</span>
                <span className="text-red-400 font-bold">:</span>
                <span className="bg-black/60 text-red-400 font-bold text-sm px-2 py-1 rounded">{String(countdown.m).padStart(2,'0')}</span>
                <span className="text-red-400 font-bold">:</span>
                <span className="bg-black/60 text-red-400 font-bold text-sm px-2 py-1 rounded">{String(countdown.s).padStart(2,'0')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Preview Dashboard - ALL LIVE DATA */}
        <div className="max-w-7xl mx-auto mt-16 px-4">
          <div className="bg-[#111111] border border-zinc-800 p-6 shadow-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <p className="text-xs tracking-[0.2em] text-zinc-500 font-bold uppercase">AI Markets</p>
              <div className="flex flex-wrap items-center gap-3">
                {/* PROMINENT REFRESH BUTTON */}
                <button 
                  onClick={fetchMarketData}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 hover:border-amber-500 transition-all disabled:opacity-50 font-bold text-sm uppercase tracking-wider"
                  data-testid="refresh-prices-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Updating...' : 'Refresh Prices'}
                </button>
                <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-800">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-zinc-400">
                    {lastUpdated ? `Updated ${lastUpdated}` : 'Auto-updates every 5 min'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* GO ECOPRENEUR - MARKET INDICES Section */}
            <div className="mb-5 bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/10 p-4 border border-amber-500/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-amber-500/20 flex items-center justify-center">
                    <LineChart className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-base font-bold text-amber-500 uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
                      GO ECOPRENEUR
                    </p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Market Indices • Real-Time</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-[10px] text-emerald-500 font-bold">LIVE</span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="bg-black/50 border border-amber-500/20 p-3 animate-pulse">
                      <div className="h-3 bg-zinc-800 rounded w-14 mb-2"></div>
                      <div className="h-5 bg-zinc-800 rounded w-20 mb-1"></div>
                      <div className="h-10 bg-zinc-800 rounded w-full mb-1"></div>
                      <div className="h-3 bg-zinc-800 rounded w-12"></div>
                    </div>
                  ))
                ) : (
                  indices
                    .filter(idx => ['S&P 500', 'Dow Jones', 'NASDAQ', 'Russell 2000', 'Volatility Index'].includes(idx.name))
                    .map((item) => {
                      const symbolMap = {
                        'S&P 500': '^GSPC',
                        'Dow Jones': '^DJI',
                        'NASDAQ': '^IXIC',
                        'Russell 2000': '^RUT',
                        'Volatility Index': '^VIX'
                      };
                      const candleSymbol = symbolMap[item.name];
                      const candles = candleData[candleSymbol] || [];
                      const isPositive = item.change_percent?.startsWith('+') || (!item.change_percent?.startsWith('-') && item.change_percent !== '0.00%');
                      
                      return (
                        <div 
                          key={item.symbol} 
                          className="bg-black/50 border border-amber-500/20 p-3 hover:border-amber-500/50 transition-all duration-300"
                          style={{ transition: 'all 0.3s ease-out' }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs text-amber-500 tracking-wider font-bold truncate">{item.name}</p>
                            <div className={`w-2 h-2 rounded-full ${isPositive ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`}></div>
                          </div>
                          <p className="text-xl font-bold font-data text-white transition-all duration-300">{item.price}</p>
                          {/* Mini Candle Chart - Synced with price */}
                          <div className="my-2">
                            <MiniCandleChart data={candles} width={130} height={50} />
                          </div>
                          <p className={`text-sm font-bold font-data transition-colors duration-300 ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                            {item.change_percent}
                          </p>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
            
            {/* STOCKS Section */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-amber-500" />
                <p className="text-xs tracking-wider text-amber-500 font-bold uppercase">Top Stocks</p>
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-zinc-800 p-2 animate-pulse">
                      <div className="h-3 bg-zinc-800 rounded w-10 mb-1"></div>
                      <div className="h-4 bg-zinc-800 rounded w-14 mb-1"></div>
                      <div className="h-3 bg-zinc-800 rounded w-10"></div>
                    </div>
                  ))
                ) : (
                  stocks.slice(0, 8).map((item) => (
                    <div key={item.symbol} className="bg-[#0A0A0A] border border-zinc-800 p-2 hover:border-amber-500/30 transition-colors">
                      <p className="text-[10px] text-zinc-500 tracking-wider font-bold">{item.symbol}</p>
                      <p className="text-sm font-bold font-data">${item.price}</p>
                      <p className={`text-[10px] font-data ${item.change_percent?.startsWith('+') ? 'text-emerald-500' : 'text-red-500'}`}>
                        {item.change_percent}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* COMMODITIES Section */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <Gem className="w-4 h-4 text-yellow-500" />
                <p className="text-xs tracking-wider text-yellow-500 font-bold uppercase">Commodities</p>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-zinc-800 p-2 animate-pulse">
                      <div className="h-3 bg-zinc-800 rounded w-10 mb-1"></div>
                      <div className="h-4 bg-zinc-800 rounded w-14 mb-1"></div>
                      <div className="h-3 bg-zinc-800 rounded w-10"></div>
                    </div>
                  ))
                ) : (
                  commodities.slice(0, 6).map((item) => (
                    <div key={item.symbol} className="bg-[#0A0A0A] border border-zinc-800 p-2 hover:border-yellow-500/30 transition-colors">
                      <p className="text-[10px] text-zinc-500 tracking-wider font-bold truncate">{item.name}</p>
                      <p className="text-sm font-bold font-data">${item.price}</p>
                      <p className={`text-[10px] font-data ${item.change_percent?.startsWith('+') ? 'text-emerald-500' : 'text-red-500'}`}>
                        {item.change_percent}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* CRYPTO Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bitcoin className="w-4 h-4 text-orange-500" />
                <p className="text-xs tracking-wider text-orange-500 font-bold uppercase">Crypto</p>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-zinc-800 p-2 animate-pulse">
                      <div className="h-3 bg-zinc-800 rounded w-10 mb-1"></div>
                      <div className="h-4 bg-zinc-800 rounded w-14 mb-1"></div>
                      <div className="h-3 bg-zinc-800 rounded w-10"></div>
                    </div>
                  ))
                ) : (
                  crypto.slice(0, 6).map((item) => (
                    <div key={item.symbol} className="bg-[#0A0A0A] border border-zinc-800 p-2 hover:border-orange-500/30 transition-colors">
                      <p className="text-[10px] text-zinc-500 tracking-wider font-bold">{item.symbol}</p>
                      <p className="text-sm font-bold font-data">${item.price}</p>
                      <p className={`text-[10px] font-data ${item.change_percent?.startsWith('+') ? 'text-emerald-500' : 'text-red-500'}`}>
                        {item.change_percent}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* POWERED BY DOSLIONS + LEGAL DISCLOSURE */}
            <div className="mt-8 pt-6 border-t border-zinc-800 flex flex-col items-center gap-4">
              <a 
                href="https://www.doslions.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/doslions-logo.webp" 
                  alt="DOSLIONS" 
                  className="h-40 w-auto"
                />
                <span className="text-sm text-amber-500 uppercase tracking-widest font-bold">Powered by DOSLIONS</span>
                <span className="text-xs text-zinc-500 hover:text-amber-500 transition-colors">www.doslions.com</span>
              </a>
              
              {/* Fun Alien Spaceship Donation Button - Compact & Engaging */}
              <div className="relative mt-6 group/donate">
                <a
                  href="https://square.link/u/3X9bvT0y"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-emerald-700 via-teal-600 to-emerald-700 rounded-full border border-emerald-500/40 hover:border-emerald-400 shadow-lg hover:shadow-emerald-500/30 transition-all duration-500"
                  data-testid="donation-btn"
                >
                  {/* Spaceship Icon - Smaller */}
                  <span className="text-lg transform group-hover/donate:translate-x-1 group-hover/donate:-translate-y-0.5 transition-transform duration-700 ease-out">🛸</span>
                  
                  {/* Text - More Visible */}
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-sm font-bold text-white tracking-wide">
                      Donate <span className="text-yellow-300">$3</span>
                    </span>
                    <span className="text-xs text-emerald-200/90 font-medium">
                      Support ECOLIONS AI Markets 💚
                    </span>
                  </span>
                  
                  {/* Slow Sparkle - Top Right */}
                  <span 
                    className="absolute -top-2 -right-2 text-yellow-400 text-base opacity-80"
                    style={{ animation: 'pulse 3s ease-in-out infinite' }}
                  >
                    ✨
                  </span>
                </a>
                
                {/* Gentle floating star - Slow animation */}
                <span 
                  className="absolute -left-3 top-1/2 -translate-y-1/2 text-cyan-400/60 text-sm"
                  style={{ animation: 'bounce 4s ease-in-out infinite' }}
                >
                  ⭐
                </span>
                
                {/* Subtitle below button */}
                <p className="text-[11px] text-emerald-400/70 mt-2 text-center font-medium">
                  Quick & Secure via Square 🔒
                </p>
              </div>
              
              <p className="text-[10px] text-zinc-500 text-center max-w-md">
                ⚠️ <span className="text-amber-500/80">NOT FINANCIAL ADVICE.</span> For educational purposes only. 
                Past performance does not guarantee future results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE AI SIGNAL PREVIEW — Blurred Teaser Wall */}
      {!user && (
        <section className="py-12 px-4 border-t border-amber-500/20 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent relative" data-testid="ai-teaser-wall">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Live AI Signals — Generating Now</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full">
                <Lock className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">Pro Only</span>
              </div>
            </div>
            
            {/* Blurred AI Signal Cards */}
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 filter blur-[6px] select-none pointer-events-none">
                <div className="bg-[#0d0d0d] border border-emerald-500/30 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-emerald-400 font-bold text-lg">NVDA</span>
                    <span className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-2 py-1">BUY SIGNAL</span>
                  </div>
                  <p className="text-2xl font-bold text-white font-data">$178.42</p>
                  <p className="text-sm text-emerald-400 font-data">+4.2% predicted</p>
                  <div className="mt-3 h-1 bg-zinc-800 rounded-full"><div className="h-full bg-emerald-500 rounded-full" style={{width:'82%'}}></div></div>
                  <p className="text-[10px] text-zinc-500 mt-1">AI Confidence: 82%</p>
                </div>
                <div className="bg-[#0d0d0d] border border-red-500/30 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-red-400 font-bold text-lg">TSLA</span>
                    <span className="bg-red-500/20 text-red-400 text-xs font-bold px-2 py-1">SELL SIGNAL</span>
                  </div>
                  <p className="text-2xl font-bold text-white font-data">$346.88</p>
                  <p className="text-sm text-red-400 font-data">-2.8% predicted</p>
                  <div className="mt-3 h-1 bg-zinc-800 rounded-full"><div className="h-full bg-red-500 rounded-full" style={{width:'74%'}}></div></div>
                  <p className="text-[10px] text-zinc-500 mt-1">AI Confidence: 74%</p>
                </div>
                <div className="bg-[#0d0d0d] border border-amber-500/30 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-amber-400 font-bold text-lg">BTC</span>
                    <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-1">HOLD</span>
                  </div>
                  <p className="text-2xl font-bold text-white font-data">$71,287.49</p>
                  <p className="text-sm text-amber-400 font-data">+1.1% predicted</p>
                  <div className="mt-3 h-1 bg-zinc-800 rounded-full"><div className="h-full bg-amber-500 rounded-full" style={{width:'68%'}}></div></div>
                  <p className="text-[10px] text-zinc-500 mt-1">AI Confidence: 68%</p>
                </div>
              </div>
              
              {/* Unlock Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[2px]">
                <Lock className="w-8 h-8 text-amber-500 mb-3" />
                <h3 className="text-xl font-bold text-white mb-1 uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
                  AI Is Working Right Now
                </h3>
                <p className="text-sm text-zinc-400 mb-4 text-center max-w-md">
                  These signals update every day. Pro Ecopreneurs are already trading with this data.
                </p>
                <Button 
                  onClick={() => navigate("/auth")}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-6 py-4 shadow-[0_0_25px_rgba(245,158,11,0.4)] group"
                  data-testid="teaser-unlock-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Unlock AI Signals — $19.97
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* AI DEVELOPMENT - THE FUTURE Section */}
      <section className="py-20 px-4 border-t border-zinc-900 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(245,158,11,0.08),transparent_70%)]"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-amber-500/10 border border-amber-500/30 mb-8 rounded-full">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-xs tracking-wider text-amber-400 font-bold uppercase">
              Powered by LIONAI
            </span>
          </div>
          
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 uppercase leading-tight"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="ai-future-heading"
          >
            <span className="text-white">AI Development Is</span><br />
            <span className="text-amber-500">The Future</span>
            <span className="text-white"> of </span>
            <span className="text-emerald-500">Accurate Decisions</span>
          </h2>
          
          <p className="text-lg text-zinc-400 max-w-3xl mx-auto mb-6 leading-relaxed">
            While others guess, <span className="text-amber-400 font-bold">our AI reads the market pulse in real-time.</span> 
            {" "}Institutional-grade intelligence that was once reserved for Wall Street — now in your hands.
          </p>
          <p className="text-sm text-zinc-500 max-w-2xl mx-auto mb-10">
            Every signal, every prediction, every insight is generated by advanced AI analyzing thousands of data points per second.
            The question isn't <span className="text-amber-400 italic">if</span> you should use AI — it's <span className="text-amber-400 italic">when</span> you'll start.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto mb-10">
            <div className="bg-black/40 border border-amber-500/20 p-6 hover:border-amber-500/50 transition-all group">
              <div className="w-12 h-12 bg-amber-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500/20 transition-colors">
                <Brain className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>AI PREDICTIONS</h3>
              <p className="text-xs text-zinc-400">Daily AI-powered signals on stocks, crypto & commodities — before the market moves.</p>
            </div>
            <div className="bg-black/40 border border-emerald-500/20 p-6 hover:border-emerald-500/50 transition-all group">
              <div className="w-12 h-12 bg-emerald-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>LIVE TRACKING</h3>
              <p className="text-xs text-zinc-400">Real-time prices, momentum bars, and trend analysis — updated every 30 seconds.</p>
            </div>
            <div className="bg-black/40 border border-purple-500/20 p-6 hover:border-purple-500/50 transition-all group">
              <div className="w-12 h-12 bg-purple-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-500/20 transition-colors">
                <Shield className="w-6 h-6 text-purple-500" />
              </div>
              <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>91% ACCURACY</h3>
              <p className="text-xs text-zinc-400">Our AI tracks its own accuracy. Transparent. Verified. No hidden agendas.</p>
            </div>
          </div>

          <Button 
            onClick={() => navigate("/auth")}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-8 py-5 text-base shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] transition-all group"
            data-testid="ai-future-cta"
          >
            <Crown className="w-5 h-5 mr-2" />
            Start Your AI Advantage
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="text-xs text-zinc-600 mt-4">Pro Ecopreneur: $19.97 first month, then $49.97/mo</p>
        </div>
      </section>

      {/* Features Section - CLICKABLE CARDS */}
      <section className="py-20 px-4 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-amber-500 mb-4 font-bold uppercase text-center">
            Platform Features
          </p>
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight text-center mb-16 uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Everything You Need
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <button 
                key={index}
                onClick={() => openServicePopup(feature.service)}
                className="bg-[#111111] border border-zinc-800 p-6 hover:border-amber-500/50 hover:bg-amber-500/5 transition-all duration-300 group cursor-pointer text-left transform hover:scale-[1.02]"
                data-testid={`feature-${feature.service}`}
              >
                <div className="w-12 h-12 bg-amber-500/10 flex items-center justify-center mb-4 text-amber-500 group-hover:bg-amber-500/20 transition-colors">
                  {feature.icon}
                </div>
                <h3 
                  className="text-lg font-semibold mb-2 tracking-wide text-white group-hover:text-amber-500 transition-colors"
                  style={{ fontFamily: 'Barlow Condensed' }}
                >
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400">{feature.description}</p>
                <div className="mt-3 flex items-center gap-1 text-xs text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-3 h-3" />
                  <span>View Options</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 border-t border-zinc-900" id="pricing">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-amber-500 mb-4 font-bold uppercase text-center">
            Simple Pricing
          </p>
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 uppercase text-center"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Choose Your Plan
          </h2>
          <p className="text-zinc-400 mb-12 text-center">
            Start free. Unlock LIONAI when you're ready.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free Plan */}
            <div className="bg-[#111111] border border-zinc-800 p-8">
              <p className="text-xs tracking-[0.3em] text-zinc-500 mb-4 font-bold uppercase">
                Free Forever
              </p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-5xl font-bold font-data">$0</span>
              </div>
              <ul className="space-y-3 mb-8">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
                <li className="flex items-center gap-3 text-sm">
                  <Lock className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                  <span className="text-zinc-500">MEET LIONAI (Locked)</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate("/auth")}
                variant="outline"
                className="w-full py-6 border-zinc-700 text-white hover:bg-zinc-800"
                data-testid="free-plan-btn"
              >
                Get Started Free
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="bg-[#111111] border-2 border-amber-500 p-8 relative shadow-[0_0_40px_rgba(245,158,11,0.15)]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-xs font-bold px-4 py-1 uppercase tracking-wider">
                Most Popular
              </div>
              <p className="text-xs tracking-[0.3em] text-amber-500 mb-4 font-bold uppercase">
                Pro Ecopreneur
              </p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-bold font-data text-amber-500">$19.97</span>
                <span className="text-zinc-500">first month</span>
              </div>
              <p className="text-sm text-zinc-400 mb-6">Then $49.97/month</p>
              <ul className="space-y-3 mb-8">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm">
                    <Check className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span className="text-zinc-200">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full py-6 bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                data-testid="pro-plan-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Start Pro Ecopreneur
              </Button>
              <p className="text-xs text-zinc-500 text-center mt-4">
                $19.97 first month, then $49.97/mo. Cancel anytime.
              </p>
              {/* Legal Disclaimer */}
              <div className="mt-4 p-3 bg-zinc-900/50 border border-zinc-800 rounded">
                <p className="text-[10px] text-zinc-600 leading-relaxed">
                  <span className="text-amber-500/70 font-bold">AI DAILY FUTURIST NUMBERS CALCULATION PREDICTION + DAILY SIGNAL</span>
                  <br />
                  <span className="text-zinc-500">⚠️ NOT FINANCIAL ADVICE. For educational and informational purposes only. Past performance does not guarantee future results. Always consult a licensed financial advisor.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET - Real AI Features Showcase */}
      <section className="py-20 px-4 border-t border-zinc-900 bg-gradient-to-b from-transparent via-amber-500/3 to-transparent" id="ai-features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-amber-500/10 border border-amber-500/30 mb-6 rounded-full">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-xs tracking-wider text-amber-400 font-bold uppercase">
                Pro Ecopreneur Exclusive
              </span>
            </div>
            <h2 
              className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 uppercase"
              style={{ fontFamily: 'Barlow Condensed' }}
              data-testid="ai-features-heading"
            >
              <span className="text-white">What's Inside </span>
              <span className="text-amber-500">Your AI Arsenal</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-base">
              Six powerful AI engines working 24/7 to give you an edge most traders only dream about.
              <span className="text-amber-400 font-bold"> All included in your Pro subscription.</span>
            </p>
          </div>

          {/* AI Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {[
              { 
                icon: <Sparkles className="w-6 h-6" />, 
                title: "AI Hot Picks", 
                tagline: "Daily AI-Curated Opportunities",
                desc: "10 hand-picked assets every day with BUY/SELL signals, RSI momentum, and mini TradingView charts. The AI spots what you'd miss.",
                color: "amber",
                badge: "MOST POPULAR"
              },
              { 
                icon: <TrendingUp className="w-6 h-6" />, 
                title: "Top Market Movers", 
                tagline: "Biggest Gainers & Losers — Live",
                desc: "Interactive grid of the 7 hottest movers with live prices, volume data, and clickable popup charts for deep analysis.",
                color: "emerald",
                badge: null
              },
              { 
                icon: <BarChart3 className="w-6 h-6" />, 
                title: "AI Whale Tracker", 
                tagline: "Follow the Institutional Money",
                desc: "See where the big money flows. Track institutional buys and insider sells before the news catches up.",
                color: "blue",
                badge: "NEW"
              },
              { 
                icon: <LineChart className="w-6 h-6" />, 
                title: "Earnings Whisper AI", 
                tagline: "Predict Before the Report Drops",
                desc: "AI-generated earnings predictions for upcoming reports. Know the whisper numbers that Wall Street won't share.",
                color: "purple",
                badge: null
              },
              { 
                icon: <MessageSquare className="w-6 h-6" />, 
                title: "Social Sentiment AI", 
                tagline: "Read the Market's Mood",
                desc: "Real-time sentiment analysis from social media, news, and forums. Know when fear or greed is driving the market.",
                color: "cyan",
                badge: null
              },
              { 
                icon: <Brain className="w-6 h-6" />, 
                title: "Economic Calendar AI", 
                tagline: "Never Get Blindsided",
                desc: "AI-powered impact predictions for every major economic event. CPI, Fed meetings, jobs reports — with forecasted market reactions.",
                color: "amber",
                badge: null
              }
            ].map((feature, index) => {
              const colorMap = {
                amber: { border: 'border-amber-500/20 hover:border-amber-500/60', bg: 'bg-amber-500/10', hoverBg: 'group-hover:bg-amber-500/20', text: 'text-amber-500', badge: 'bg-amber-500 text-black' },
                emerald: { border: 'border-emerald-500/20 hover:border-emerald-500/60', bg: 'bg-emerald-500/10', hoverBg: 'group-hover:bg-emerald-500/20', text: 'text-emerald-500', badge: 'bg-emerald-500 text-black' },
                blue: { border: 'border-blue-500/20 hover:border-blue-500/60', bg: 'bg-blue-500/10', hoverBg: 'group-hover:bg-blue-500/20', text: 'text-blue-500', badge: 'bg-blue-500 text-white' },
                purple: { border: 'border-purple-500/20 hover:border-purple-500/60', bg: 'bg-purple-500/10', hoverBg: 'group-hover:bg-purple-500/20', text: 'text-purple-500', badge: 'bg-purple-500 text-white' },
                cyan: { border: 'border-cyan-500/20 hover:border-cyan-500/60', bg: 'bg-cyan-500/10', hoverBg: 'group-hover:bg-cyan-500/20', text: 'text-cyan-500', badge: 'bg-cyan-500 text-black' },
              };
              const c = colorMap[feature.color];
              return (
                <div 
                  key={index} 
                  className={`bg-[#0d0d0d] border ${c.border} p-6 transition-all duration-300 group relative cursor-pointer hover:translate-y-[-2px]`}
                  onClick={() => navigate("/auth")}
                  data-testid={`feature-card-${index}`}
                >
                  {feature.badge && (
                    <div className={`absolute -top-2.5 right-4 ${c.badge} text-[10px] font-bold px-3 py-0.5 uppercase tracking-wider`}>
                      {feature.badge}
                    </div>
                  )}
                  <div className={`w-11 h-11 ${c.bg} ${c.hoverBg} flex items-center justify-center mb-4 ${c.text} transition-colors`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1 uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>
                    {feature.title}
                  </h3>
                  <p className={`text-xs ${c.text} font-bold mb-3 uppercase tracking-wider`}>{feature.tagline}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">{feature.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-xs text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Lock className="w-3 h-3" />
                    <span>Unlock with Pro</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Conversion CTA */}
          <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-amber-500/10 border-2 border-amber-500/50 p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(245,158,11,0.12),transparent_60%)]"></div>
            <div className="relative z-10">
              <Crown className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h3 
                className="text-2xl sm:text-3xl font-bold mb-3 uppercase"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                <span className="text-white">All 6 AI Engines. </span>
                <span className="text-amber-500">One Subscription.</span>
              </h3>
              <p className="text-zinc-400 text-sm mb-6 max-w-xl mx-auto">
                Stop paying for scattered tools. Get everything in one place — for less than a daily coffee.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
                <div className="text-center">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold font-data text-amber-500">$19.97</span>
                    <span className="text-zinc-400 text-sm">first month</span>
                  </div>
                  <p className="text-xs text-zinc-500">Then $49.97/mo</p>
                </div>
                <Button 
                  onClick={() => navigate("/auth")}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-8 py-5 text-base shadow-[0_0_25px_rgba(245,158,11,0.4)] hover:shadow-[0_0_35px_rgba(245,158,11,0.6)] transition-all group"
                  data-testid="features-cta-btn"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  Unlock All AI Features
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              <p className="text-xs text-zinc-600">Cancel anytime. No contracts. No surprises.</p>
            </div>
          </div>
          
          <p className="text-[10px] text-zinc-600 mt-6 text-center">
            ⚠️ AI predictions are for educational purposes only. NOT FINANCIAL ADVICE. Past performance does not guarantee future results.
          </p>
        </div>
      </section>

      {/* ELEGANT PRO ECOPRENEUR SALES PITCH */}
      {/* BULL vs BEAR — Fun Engagement Arena */}
      <section className="py-20 px-4 border-t border-zinc-900 bg-gradient-to-b from-emerald-500/3 via-transparent to-red-500/3 relative overflow-hidden" id="bull-bear-arena">
        <style>{`
          @keyframes bullFloat {
            0%, 100% { transform: translateY(0) translateX(0) rotate(-2deg); }
            25% { transform: translateY(-12px) translateX(8px) rotate(0deg); }
            50% { transform: translateY(-4px) translateX(15px) rotate(2deg); }
            75% { transform: translateY(-16px) translateX(5px) rotate(-1deg); }
          }
          @keyframes bearFloat {
            0%, 100% { transform: translateY(0) translateX(0) rotate(2deg); }
            25% { transform: translateY(-10px) translateX(-8px) rotate(0deg); }
            50% { transform: translateY(-18px) translateX(-12px) rotate(-2deg); }
            75% { transform: translateY(-6px) translateX(-5px) rotate(1deg); }
          }
          @keyframes greenPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(34,197,94,0.3); }
            50% { box-shadow: 0 0 40px rgba(34,197,94,0.6), 0 0 80px rgba(34,197,94,0.2); }
          }
          @keyframes redPulse {
            0%, 100% { box-shadow: 0 0 20px rgba(239,68,68,0.3); }
            50% { box-shadow: 0 0 40px rgba(239,68,68,0.6), 0 0 80px rgba(239,68,68,0.2); }
          }
          @keyframes sparkTrail {
            0% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(0) translateY(20px); }
          }
          @keyframes vsGlow {
            0%, 100% { text-shadow: 0 0 20px rgba(245,158,11,0.5); }
            50% { text-shadow: 0 0 40px rgba(245,158,11,0.9), 0 0 60px rgba(245,158,11,0.4); }
          }
        `}</style>

        <div className="max-w-5xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-amber-500/10 border border-amber-500/30 mb-8 rounded-full">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-xs tracking-wider text-amber-400 font-bold uppercase">
              Live Market Battle
            </span>
          </div>

          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="bull-bear-heading"
          >
            <span className="text-emerald-500">Bulls</span>
            <span className="text-zinc-500 mx-3" style={{ animation: 'vsGlow 2s ease-in-out infinite' }}>vs</span>
            <span className="text-red-500">Bears</span>
          </h2>
          <p className="text-base text-zinc-400 mb-12 max-w-xl mx-auto">
            The eternal battle. Which side are you on today?<br />
            <span className="text-amber-400 font-bold">Our AI reads both sides so you don't have to guess.</span>
          </p>

          {/* Battle Arena */}
          <div className="flex items-center justify-center gap-6 sm:gap-12 mb-12">
            {/* BULL Side */}
            <div className="flex flex-col items-center" data-testid="bull-side">
              <div 
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-900/30 border-2 border-emerald-500/50 flex items-center justify-center relative"
                style={{ animation: 'bullFloat 4s ease-in-out infinite, greenPulse 3s ease-in-out infinite' }}
              >
                <span className="text-5xl sm:text-6xl select-none" role="img" aria-label="bull">&#x1F402;</span>
                {/* Spark trail */}
                <div className="absolute -bottom-2 left-1/2 w-3 h-3 bg-emerald-500 rounded-full" style={{ animation: 'sparkTrail 1.5s ease-out infinite' }}></div>
                <div className="absolute -bottom-1 left-1/3 w-2 h-2 bg-emerald-400 rounded-full" style={{ animation: 'sparkTrail 1.8s ease-out infinite 0.3s' }}></div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-emerald-500 font-bold text-lg uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>Bull Mode</p>
                <p className="text-xs text-emerald-400/70">Markets going UP</p>
                <div className="flex items-center gap-1 mt-2 justify-center">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-emerald-500 font-bold font-mono text-sm">+2.4%</span>
                </div>
              </div>
            </div>

            {/* VS Center */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-amber-500/20 border-2 border-amber-500 flex items-center justify-center" style={{ animation: 'vsGlow 2s ease-in-out infinite' }}>
                <span className="text-amber-500 font-bold text-xl sm:text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>VS</span>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">Who wins?</p>
            </div>

            {/* BEAR Side */}
            <div className="flex flex-col items-center" data-testid="bear-side">
              <div 
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-br from-red-500/20 to-red-900/30 border-2 border-red-500/50 flex items-center justify-center relative"
                style={{ animation: 'bearFloat 4.5s ease-in-out infinite, redPulse 3s ease-in-out infinite' }}
              >
                <span className="text-5xl sm:text-6xl select-none" role="img" aria-label="bear">&#x1F43B;</span>
                <div className="absolute -bottom-2 right-1/2 w-3 h-3 bg-red-500 rounded-full" style={{ animation: 'sparkTrail 1.5s ease-out infinite' }}></div>
                <div className="absolute -bottom-1 right-1/3 w-2 h-2 bg-red-400 rounded-full" style={{ animation: 'sparkTrail 1.8s ease-out infinite 0.5s' }}></div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-red-500 font-bold text-lg uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>Bear Mode</p>
                <p className="text-xs text-red-400/70">Markets going DOWN</p>
                <div className="flex items-center gap-1 mt-2 justify-center">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  <span className="text-red-500 font-bold font-mono text-sm">-1.8%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fun Taglines */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 max-w-3xl mx-auto">
            <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 rounded-lg">
              <TrendingUp className="w-5 h-5 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm text-emerald-400 font-bold">Bulls say:</p>
              <p className="text-xs text-zinc-400 italic">"Buy the dip, ride the rocket!"</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded-lg">
              <Sparkles className="w-5 h-5 text-amber-500 mx-auto mb-2" />
              <p className="text-sm text-amber-400 font-bold">LIONAI says:</p>
              <p className="text-xs text-zinc-400 italic">"Let data decide, not emotions."</p>
            </div>
            <div className="bg-red-500/5 border border-red-500/20 p-4 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-400 font-bold">Bears say:</p>
              <p className="text-xs text-zinc-400 italic">"Cash is king. Wait for the drop!"</p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-black/40 border border-amber-500/30 p-8 rounded-lg max-w-2xl mx-auto">
            <p className="text-lg text-white font-bold mb-2">
              Don't pick sides. <span className="text-amber-500">Pick LIONAI.</span>
            </p>
            <p className="text-sm text-zinc-400 mb-6">
              Our AI tracks both bull and bear signals across 500+ assets — so you always know which way the wind is blowing.
            </p>
            <Button 
              onClick={() => navigate("/auth")}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-8 py-5 text-base shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] transition-all group"
              data-testid="bull-bear-cta"
            >
              <Crown className="w-5 h-5 mr-2" />
              Join the Smart Side — $19.97
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-xs text-zinc-600 mt-3">First month $19.97, then $49.97/mo. Cancel anytime.</p>
          </div>

          <p className="text-[10px] text-zinc-600 mt-8">
            AI Daily Futurist Numbers Calculation Prediction + Daily Signal. NOT FINANCIAL ADVICE. For educational purposes only.
          </p>
        </div>
      </section>

      {/* Success Metrics - Social Proof */}
      <section className="py-16 px-4 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs tracking-[0.3em] text-amber-500 mb-4 font-bold uppercase text-center">
            Trusted By Ecopreneurs
          </p>
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight text-center mb-12 uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            AI-Powered <span className="text-amber-500">Results</span>
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            <div className="text-center">
              <p className="text-5xl font-bold font-data text-amber-500">500+</p>
              <p className="text-sm text-zinc-400 mt-2">Active Ecopreneurs</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold font-data text-emerald-500">10K+</p>
              <p className="text-sm text-zinc-400 mt-2">LIONAI Insights</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold font-data text-white">14</p>
              <p className="text-sm text-zinc-400 mt-2">Markets Covered</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold font-data text-blue-500">24/7</p>
              <p className="text-sm text-zinc-400 mt-2">AI Available</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Carlos M.", role: "Project Developer", quote: "LIONAI helped me track NVDA data before the AI boom. The time-saving insights are incredibly useful.", avatar: "C" },
              { name: "Maria L.", role: "Ecopreneur", quote: "The gold tracking was spot on. Finally an AI that helps me save hours of research time.", avatar: "M" },
              { name: "James R.", role: "Business Owner", quote: "I use ECOLIONS daily. The watchlist feature and AI analysis save me hours of project research.", avatar: "J" }
            ].map((story, index) => (
              <div key={index} className="bg-[#111111] border border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-500/20 flex items-center justify-center text-amber-500 font-bold">
                    {story.avatar}
                  </div>
                  <div>
                    <p className="font-semibold">{story.name}</p>
                    <p className="text-xs text-zinc-500">{story.role}</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-400 italic">"{story.quote}"</p>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-zinc-600 mt-6 text-center">
            Results vary. Past performance doesn't guarantee future results. Not financial advice.
          </p>
        </div>
      </section>

      {/* INSTALL APP Section - PWA Promotion */}
      <section className="py-16 px-4 border-t border-zinc-900 bg-gradient-to-b from-emerald-500/5 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-4">
              <Smartphone className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-bold text-emerald-500 uppercase tracking-wider">Free Mobile App</span>
            </div>
            <h2 
              className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 uppercase"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Install <span className="text-emerald-500">ECOLIONS</span> on Your Phone
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Get instant access to market data from your home screen. No app store needed - install directly from your browser!
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* iPhone Instructions */}
            <div className="bg-[#111111] border border-zinc-800 rounded-xl p-6 hover:border-blue-500/30 transition-colors">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🍎</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white">iPhone / iPad</h3>
                  <p className="text-xs text-zinc-500">Safari Browser</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-blue-500">1</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Open in Safari</p>
                    <p className="text-xs text-zinc-500">Visit ecolionsmarkets.online</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-blue-500">2</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Tap Share Button</p>
                    <p className="text-xs text-zinc-500">Square icon with arrow at bottom</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-emerald-500">3</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">"Add to Home Screen"</p>
                    <p className="text-xs text-zinc-500">Scroll down and tap to install</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Android Instructions */}
            <div className="bg-[#111111] border border-zinc-800 rounded-xl p-6 hover:border-emerald-500/30 transition-colors">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🤖</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white">Android</h3>
                  <p className="text-xs text-zinc-500">Chrome Browser</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-emerald-500">1</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Open in Chrome</p>
                    <p className="text-xs text-zinc-500">Visit ecolionsmarkets.online</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-emerald-500">2</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Tap Menu (⋮)</p>
                    <p className="text-xs text-zinc-500">Three dots at top right</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-amber-500">3</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">"Install App"</p>
                    <p className="text-xs text-zinc-500">Or "Add to Home Screen"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* QR Code Quick Install Button */}
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowQRPopup(true)}
              className="group relative flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-emerald-600/20 via-cyan-600/20 to-emerald-600/20 border border-emerald-500/30 rounded-2xl hover:border-emerald-400/50 hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all duration-300"
              data-testid="qr-install-btn"
            >
              {/* Alien UFO */}
              <span className="text-3xl group-hover:animate-bounce" style={{ animationDuration: '1s' }}>🛸</span>
              
              {/* Text */}
              <div className="text-left">
                <p className="text-base font-bold text-white group-hover:text-emerald-300 transition-colors">
                  Scan QR Code to Install
                </p>
                <p className="text-xs text-zinc-400 flex items-center gap-1">
                  <span>powered by</span>
                  <span className="text-amber-500 font-bold">DOSLIONS</span>
                  <span className="text-lg">👽</span>
                </p>
              </div>
              
              {/* QR Icon */}
              <div className="w-12 h-12 bg-black/50 rounded-lg border border-emerald-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm8-2h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h2v2h-2v-2zm0 4h2v2h-2v-2zm-4 0h2v2h-2v-2zm2 2h2v2h-2v-2zm2 0h2v2h-2v-2z"/>
                </svg>
              </div>
              
              {/* Sparkle effects */}
              <span className="absolute -top-1 -right-1 text-yellow-400 text-sm animate-pulse">✨</span>
              <span className="absolute -bottom-1 -left-1 text-cyan-400 text-xs animate-ping opacity-75">💫</span>
            </button>
          </div>
          
          {/* Benefits */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-zinc-300">Works Offline</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-zinc-300">No App Store</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-zinc-300">100% Free</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 rounded-full">
              <Check className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-zinc-300">Full Screen Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsor Section */}
      <section className="py-16 px-4 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto text-center">
          <a 
            href="https://www.doslions.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block group"
          >
            <img 
              src={LOGO_URL} 
              alt="DOSLIONS" 
              className="w-32 h-32 object-contain mx-auto mb-4 group-hover:scale-105 transition-transform"
            />
            <p className="text-lg text-zinc-400 group-hover:text-amber-500 transition-colors">
              A <strong>doslions.com</strong> product
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              ART • BRAND • ECOPRENEUR
            </p>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 border-t border-zinc-900 bg-gradient-to-b from-transparent to-amber-500/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            <span className="text-amber-500">TIME</span> Is <span className="text-emerald-500">VALUE</span>
          </h2>
          <p className="text-zinc-400 mb-8">
            Every minute counts. Let LIONAI work for you 24/7. Join the ecopreneur movement today.
          </p>
          <Button 
            onClick={() => navigate("/auth")}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-10 py-6 text-lg shadow-[0_0_30px_rgba(245,158,11,0.4)]"
            data-testid="cta-btn"
          >
            Start Now <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
            <span className="font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              <span className="text-amber-500">ECOLIONS</span> AI MARKETS
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <button onClick={() => navigate("/about")} className="hover:text-amber-500 font-semibold uppercase tracking-wider text-xs">
              About ECOLIONS
            </button>
            <span>|</span>
            <a href="https://www.doslions.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 flex items-center gap-1">
              doslions.com <ExternalLink className="w-3 h-3" />
            </a>
            <span>|</span>
            <a href="mailto:doslionsteam11@gmail.com" className="hover:text-amber-500">Contact</a>
          </div>
          <p className="text-sm text-zinc-600">
            © 2026 ECOLIONS. Not financial advice.
          </p>
        </div>
      </footer>

      {/* SERVICE POPUP - Sales/Support Window */}
      {showServicePopup && selectedService && serviceDetails[selectedService] && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#111111] border border-amber-500/30 w-full max-w-md shadow-2xl relative animate-in fade-in zoom-in duration-200">
            {/* Close Button */}
            <button 
              onClick={() => setShowServicePopup(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
              data-testid="close-service-popup"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Header */}
            <div className="p-6 border-b border-zinc-800 bg-gradient-to-r from-amber-500/10 to-transparent">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                    {serviceDetails[selectedService].title}
                  </h3>
                  <p className="text-sm text-zinc-400">{serviceDetails[selectedService].subtitle}</p>
                </div>
              </div>
            </div>
            
            {/* Options */}
            <div className="p-6 space-y-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-4">Choose Your Plan</p>
              {serviceDetails[selectedService].options.map((option, index) => (
                <button 
                  key={index}
                  onClick={() => {
                    option.action();
                    setShowServicePopup(false);
                  }}
                  className={`w-full p-4 border transition-all duration-200 text-left group ${
                    option.primary 
                      ? 'border-amber-500 bg-amber-500/10 hover:bg-amber-500/20' 
                      : 'border-zinc-700 bg-zinc-900 hover:border-amber-500/50 hover:bg-zinc-800'
                  }`}
                  data-testid={`service-option-${index}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-bold ${option.primary ? 'text-amber-500' : 'text-white'}`}>
                      {option.name}
                    </span>
                    <span className="text-lg font-bold font-data text-white">{option.price}</span>
                  </div>
                  <p className="text-sm text-zinc-400">{option.desc}</p>
                  {option.primary && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-500">
                      <CreditCard className="w-4 h-4" />
                      <span>Secure payment via Square</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
              <p className="text-xs text-zinc-500 text-center">
                🔒 Secure checkout • Cancel anytime • No hidden fees
              </p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-xs text-zinc-600">Powered by</span>
                <span className="text-xs font-bold text-white">Square</span>
                <span className="text-xs text-zinc-600">+</span>
                <span className="text-xs font-bold text-amber-500">PayPal</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PRO ECOPRENEUR QUICK CHECKOUT POPUP */}
      {showProPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
          <div className="bg-[#111111] border-2 border-amber-500 w-full max-w-lg shadow-[0_0_50px_rgba(245,158,11,0.3)] relative animate-in fade-in zoom-in duration-200">
            {/* Close Button */}
            <button 
              onClick={() => setShowProPopup(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors z-10"
              data-testid="close-pro-popup"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Header with Crown */}
            <div className="p-6 border-b border-amber-500/30 bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent text-center">
              <div className="w-16 h-16 bg-amber-500/30 mx-auto flex items-center justify-center mb-4 rounded-full">
                <Crown className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-amber-500 uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
                Pro Ecopreneur
              </h3>
              <p className="text-sm text-zinc-400 mt-2">Unlock the full power of LIONAI</p>
            </div>
            
            {/* Pricing */}
            <div className="p-6 text-center border-b border-zinc-800">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-4xl font-bold text-white">$19.97</span>
                <span className="text-sm text-zinc-500">/first month</span>
              </div>
              <p className="text-xs text-zinc-500">Then $49.97/month • Cancel anytime</p>
            </div>
            
            {/* Features */}
            <div className="p-6 space-y-3">
              {[
                "MEET LIONAI (Pay to build)",
                "AI Daily Insights & Signals",
                "Weekly AI Trending Picks",
                "Sports AI Predictions",
                "Full Market Access",
                "Priority Support"
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-emerald-500" />
                  </div>
                  <span className="text-sm text-zinc-300">{feature}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="p-6 space-y-3 bg-zinc-900/50">
              <Button 
                onClick={() => {
                  setShowProPopup(false);
                  navigate("/auth?plan=pro");
                }}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6 text-lg shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                data-testid="pro-checkout-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Get Started Now
              </Button>
              <p className="text-xs text-zinc-500 text-center">
                🔒 Secure payment with Square • Phone confirmation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FREE VIEW BLOCKER - Shows after 1 hour */}
      {showFreeBlocker && !user && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-lg">
          <div className="bg-[#111111] border-2 border-amber-500/50 w-full max-w-md shadow-2xl relative animate-in fade-in zoom-in duration-200">
            {/* Warning Icon */}
            <div className="p-8 text-center border-b border-zinc-800">
              <div className="w-20 h-20 bg-amber-500/20 mx-auto flex items-center justify-center mb-4 rounded-full animate-pulse">
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-white uppercase tracking-wider mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Free View Time Expired
              </h3>
              <p className="text-sm text-zinc-400">
                Your 1-hour free market access has ended
              </p>
            </div>
            
            {/* Options */}
            <div className="p-6 space-y-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold text-center mb-4">
                Choose to continue
              </p>
              
              {/* Option 1: Become Pro */}
              <Button 
                onClick={() => {
                  setShowFreeBlocker(false);
                  navigate("/auth?plan=pro");
                }}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-5 text-base"
                data-testid="blocker-pro-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Become Pro Ecopreneur - $19.97
              </Button>
              
              {/* Option 2: Create Free Account */}
              <Button 
                onClick={() => {
                  setShowFreeBlocker(false);
                  navigate("/auth");
                }}
                variant="outline"
                className="w-full border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 font-bold uppercase tracking-wider py-5"
                data-testid="blocker-account-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Create Free Account
              </Button>
              
              {/* Option 3: Refresh for another hour */}
              <button 
                onClick={resetFreeViewTimer}
                className="w-full text-center text-xs text-zinc-600 hover:text-zinc-400 py-2 underline transition-colors"
                data-testid="blocker-refresh-btn"
              >
                Continue with another 1-hour free view
              </button>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
              <p className="text-xs text-zinc-500 text-center">
                <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
                Free users don't have dashboard access. Create an account to unlock more features.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
